import * as React from 'react';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  CustomTooltipInfo,
  Icon,
  Pill,
  Text,
  getIcon,
} from '@quality24/design-system';
import classNames from 'classnames';

import { DateFilter, DynamicCardSubcomponentData } from '../../../../@types';
import { filterData } from '../../utils/filter';
import { createReferenceMap } from '../../utils/reference';
import { mapData } from '../../utils/mapper';
import { parseDataValue, sanitizeKey } from '../../../../utils';

import styles from './CardContainer.module.scss';

export interface Props
  extends Omit<React.HTMLAttributes<HTMLElement>, 'className'> {
  className?: DynamicCardSubcomponentData['className'];
  data: Record<string, Record<string, unknown>>;
  dateFilter: DateFilter;
  config: Omit<DynamicCardSubcomponentData, 'type' | 'className'>;
}

/**
 * <DynamicCardSection> page
 */
export const DynamicCardSection: React.FunctionComponent<Props> = ({
  className,
  data,
  dateFilter,
  config: {
    label,
    tooltipContent,
    icon,
    value,
    badgeKey,
    postQuery,
    rootKey,
    references,
    aggrKey,
    groupBy,
    postMap,
    rowKey,
    rowKeyValue,
  },
  ...htmlElementProps
}) => {
  /**
   * Gets the aggregation data for this container
   */
  const aggrData = React.useMemo(
    () => filterData(data, rootKey, postQuery),
    [data, rootKey, postQuery],
  );

  /**
   * Creates the reference map
   */
  const referenceMap = React.useMemo(
    () => createReferenceMap({ data, references, dateFilter, postQuery }),
    [data, references, dateFilter, postQuery],
  );

  /**
   * Maps data
   */
  const mappedData = React.useMemo(
    () =>
      mapData({
        data: aggrData,
        groupBy,
        aggrKey,
        reference: referenceMap,
      }) as Array<Record<string, string | number>>,
    [aggrData, groupBy, aggrKey, referenceMap],
  );

  /**
   * Gets the filtered data after mapping
   */
  const filteredData = React.useMemo(
    () => filterData(mappedData, undefined, postMap),
    [mappedData, postMap],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('DynamicCardContainer', {
      originalData: data,
      aggregatedData: aggrData,
      referenceMap,
      mappedData,
      filteredData,
    });
  }

  const dataToDisplay = React.useMemo(() => {
    if (rowKey && rowKeyValue) {
      return filteredData.find(
        (d) => `${d[sanitizeKey(rowKey)]}` === rowKeyValue,
      );
    }
    return filteredData[0];
  }, [filteredData, rowKey, rowKeyValue]);

  return (
    <div
      className={classNames(styles['section-wrapper'], className?.container)}
      {...htmlElementProps}
    >
      {label && (
        <div className="d-flex gap-4">
          <Text className={className?.label}>
            {dataToDisplay
              ? parseDataValue(dataToDisplay, label, 'string') ?? '-'
              : label ?? '-'}
          </Text>

          {tooltipContent && <CustomTooltipInfo info={tooltipContent} />}
        </div>
      )}

      <div
        className={classNames(
          'd-flex gap-2 flex-items-center',
          className?.content,
        )}
      >
        <Text className={classNames('d-flex', className?.value)}>
          {icon && (
            <Icon
              icon={getIcon(icon) || faCircle}
              className={classNames('mr-2', className?.icon)}
            />
          )}
          <span
            className={className?.innerValue}
            title={
              dataToDisplay &&
              typeof parseDataValue(dataToDisplay, value, 'string') === 'string'
                ? (parseDataValue(dataToDisplay, value, 'string') as string)
                : '-'
            }
          >
            {dataToDisplay
              ? parseDataValue(dataToDisplay, value, 'string') ?? '-'
              : '-'}
          </span>
        </Text>
        {!!badgeKey && !!dataToDisplay && (
          <Pill
            value={String(parseDataValue(dataToDisplay, badgeKey, 'string'))}
          />
        )}
      </div>
    </div>
  );
};

export default DynamicCardSection;
